// These three come straight from the zod contracts rather than being restated
// here: they are new, and a hand-copy would drift the moment the schema moves.
import type {
  AppRequest,
  AppRequestStatus,
  AccountBudget,
  AdProvider,
  TransactionType,
  BudgetSummary,
  Campaign,
  CampaignDailyPoint,
  CreateAppRequestBody,
  CreateDepositWalletBody,
  DepositWallet,
  EditTopUpBody,
  EditTopUpResult,
  MiniAppContext,
  NotificationPrefs,
  SpendSeries,
  TopUpWithClient,
  UpdateClientBody,
} from "@app/shared";

const TOKEN_KEY = "moloco_tracker_token";

export const tokenStore = {
  get: () => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set: (token: string) => {
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch {
      /* private browsing */
    }
  },
  clear: () => {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* private browsing */
    }
  },
};

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
  }
}

export interface CampaignSpendQuery {
  adAccountId?: number;
  clientId?: number;
  provider?: string;
  start: string;
  end: string;
}

export interface CampaignSpendRow {
  campaignId: number;
  title: string;
  provider: string;
  state: string | null;
  enablingState: string | null;
  adAccountId: number;
  accountTitle: string;
  clientId: number | null;
  clientTitle: string | null;
  spend: string;
  impressions: number;
  clicks: number;
  activeDays: number;
}

export interface BudgetRollup {
  campaignCount: number;
  liveCampaignCount: number;
  dailyBudgetLive: string;
  dailyBudgetAll: string;
  spendToday: string;
}

export interface ClientBudget extends BudgetRollup {
  clientId: number | null;
  clientTitle: string | null;
  cabinets: (BudgetRollup & { adAccountId: number; title: string; provider: string })[];
}

export interface ProviderBalance {
  provider: string;
  cabinets: number;
  /** Paid onto this platform's account: money not on a cabinet yet. */
  accountFunded: string;
  /** What can still be moved onto a cabinet; for Moloco, the master balance. */
  accountBalance: string;
  spendToday: string;
  netFunded: string;
  totalSpend: string;
  availableBalance: string;
}

export interface ClientTransferParts {
  accounts: boolean;
  payments: boolean;
  tiers: boolean;
  appRequests: boolean;
  history: boolean;
}

export interface ClientTransferBody {
  toClientId: number;
  parts: ClientTransferParts;
  deleteSource: boolean;
}

export interface ClientTransferResult {
  toClientId: number;
  accounts: number;
  topUps: number;
  ledger: number;
  tiers: number;
  appRequests: number;
  notifications: number;
  deliveries: number;
  sourceDeleted: boolean;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = tokenStore.get();
  // Content-Type only when there is actually a body: fastify rejects an empty
  // body that claims to be JSON, which silently broke every bodyless POST.
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: {
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });

  const body = (await res.json().catch(() => null)) as { success: boolean; data: unknown } | null;

  if (!res.ok || !body?.success) {
    const detail = body?.data as { error?: string } | undefined;
    // A 401 anywhere means the session is gone; drop it so the router bounces
    // to the login screen instead of looping on failed requests.
    if (res.status === 401) tokenStore.clear();
    throw new ApiError(detail?.error ?? `HTTP ${res.status}`, res.status);
  }
  return body.data as T;
}

/**
 * Downloads a binary endpoint. `request` is JSON-only -- it parses every
 * response body -- and an <a download> cannot carry the bearer token, so the
 * file is fetched, turned into a blob URL and handed to a synthetic click.
 */
async function download(path: string, fallbackName: string): Promise<void> {
  const token = tokenStore.get();
  const res = await fetch(`/api${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!res.ok) {
    if (res.status === 401) tokenStore.clear();
    // The failure body is still JSON, so the real reason survives.
    const body = (await res.json().catch(() => null)) as { data?: { error?: string } } | null;
    throw new ApiError(body?.data?.error ?? `HTTP ${res.status}`, res.status);
  }

  const disposition = res.headers.get("Content-Disposition") ?? "";
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  const plain = /filename="([^"]+)"/i.exec(disposition);
  const name = utf8 ? decodeURIComponent(utf8[1]!) : (plain?.[1] ?? fallbackName);

  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  // Revoked on the next tick: revoking synchronously races the download in
  // some browsers and produces an empty file.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Drops undefined keys so `?clientId=undefined` never reaches the server. */
const qs = (params: Record<string, string | number | undefined>) => {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined);
  return entries.length === 0 ? "" : `?${new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString()}`;
};

export const api = {
  login: (email: string, password: string) =>
    request<{ token: string; email: string }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  me: () => request<Me>("/me"),
  broadcasts: () => request<Broadcast[]>("/broadcast"),
  broadcast: (id: number) => request<Broadcast>(`/broadcast/${id}`),
  broadcastDeliveries: (id: number) => request<BroadcastDelivery[]>(`/broadcast/${id}/deliveries`),
  sendBroadcast: (body: NewBroadcast) =>
    request<{ id: number; scheduled: boolean; scheduledAt: string | null }>("/broadcast", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  cancelBroadcast: (id: number) => request<{ cancelled: boolean }>(`/broadcast/${id}`, { method: "DELETE" }),
  deleteBroadcastMessages: (id: number) =>
    request<{ queued: number }>(`/broadcast/${id}/delete-messages`, { method: "POST" }),
  broadcastPreview: (audience: string, targetClientIds: number[]) =>
    request<{ recipients: number }>("/broadcast/preview", {
      method: "POST",
      body: JSON.stringify({ audience, targetClientIds }),
    }),
  uploadBroadcastMedia: async (file: File) => {
    const form = new FormData();
    form.append("file", file);
    // FormData sets its own multipart boundary, so no Content-Type here.
    const res = await fetch("/api/broadcast/upload", {
      method: "POST",
      headers: { ...(tokenStore.get() ? { Authorization: `Bearer ${tokenStore.get()}` } : {}) },
      body: form,
    });
    const body = (await res.json().catch(() => null)) as { success: boolean; data: unknown } | null;
    if (!res.ok || !body?.success) throw new ApiError("UPLOAD_FAILED", res.status);
    return body.data as { path: string; name: string; mime: string };
  },
  permissions: () => request<PermissionDef[]>("/permissions"),
  roles: () => request<Role[]>("/roles"),
  createRole: (name: string, permissions: string[]) =>
    request<{ id: number }>("/roles", { method: "POST", body: JSON.stringify({ name, permissions }) }),
  updateRole: (id: number, patch: { name?: string; permissions?: string[] }) =>
    request<{ id: number }>(`/roles/${id}`, { method: "PUT", body: JSON.stringify(patch) }),
  deleteRole: (id: number) => request<{ deleted: boolean }>(`/roles/${id}`, { method: "DELETE" }),
  users: () => request<AdminUser[]>("/users"),
  createUser: (body: { email: string; password: string; name?: string; roleId: number }) =>
    request<{ id: number }>("/users", { method: "POST", body: JSON.stringify(body) }),
  updateUser: (id: number, patch: { password?: string; name?: string; roleId?: number; isActive?: boolean }) =>
    request<{ updated: boolean }>(`/users/${id}`, { method: "PUT", body: JSON.stringify(patch) }),
  deleteUser: (id: number) => request<{ deleted: boolean }>(`/users/${id}`, { method: "DELETE" }),
  deleteClient: (id: number) =>
    request<{ deleted: boolean; releasedAccounts: number }>(`/clients/${id}`, { method: "DELETE" }),
  stats: (provider?: AdProvider, range?: { start: string; end: string }) =>
    request<Stats>(`/stats${qs({ provider, start: range?.start, end: range?.end })}`),
  statsSpend: (days = 30, provider?: AdProvider, range?: { start: string; end: string }) =>
    request<SpendSeries>(`/stats/spend${qs({ days, provider, start: range?.start, end: range?.end })}`),
  topAccounts: (provider?: AdProvider) => request<TopAccount[]>(`/stats/top-accounts${qs({ provider })}`),
  /** Money and spend per client, narrowed to one platform when asked. */
  statsClients: (provider?: AdProvider) =>
    request<
      {
        clientId: number;
        title: string;
        cabinets: number;
        netFunded: string;
        totalSpend: string;
        spendToday: string;
        availableBalance: string;
      }[]
    >(`/stats/clients${qs({ provider })}`),
  clients: () => request<ClientOverview[]>("/clients"),
  client: (id: number) => request<ClientOverview>(`/clients/${id}`),
  createClient: (telegramChatId: string, title: string) =>
    request<{ id: number }>("/clients", { method: "POST", body: JSON.stringify({ telegramChatId, title }) }),
  tiers: (id: number) => request<Tier[]>(`/clients/${id}/tiers`),
  setTiers: (id: number, tiers: Tier[]) =>
    request<Tier[]>(`/clients/${id}/tiers`, { method: "PUT", body: JSON.stringify({ tiers }) }),
  topUps: (id: number) => request<TopUp[]>(`/clients/${id}/top-ups`),
  /** Every deposit across every client; not narrowed by platform. */
  allTopUps: (range?: { start: string; end: string }, provider?: AdProvider) =>
    request<TopUpWithClient[]>(`/top-ups${qs({ start: range?.start, end: range?.end, provider })}`),
  /** Per-platform balance: that platform's transactions minus its spend. */
  balances: (id: number) => request<ProviderBalance[]>(`/clients/${id}/balances`),
  /** Which clients a cabinet worked for, and when. */
  accountAssignments: (adAccountId: number) => request<AccountAssignment[]>(`/accounts/${adAccountId}/assignments`),
  /** Which cabinets a client had, and when. */
  clientAssignments: (clientId: number) => request<AccountAssignment[]>(`/clients/${clientId}/assignments`),
  addAssignment: (body: {
    adAccountId: number;
    clientId: number;
    startedAt: string;
    endedAt?: string | null;
    note?: string;
  }) => request<{ id: number }>("/account-assignments", { method: "POST", body: JSON.stringify(body) }),
  updateAssignment: (
    id: number,
    body: { clientId?: number; startedAt?: string; endedAt?: string | null; note?: string | null },
  ) => request<{ id: number }>(`/account-assignments/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteAssignment: (id: number) => request<{ id: number }>(`/account-assignments/${id}`, { method: "DELETE" }),
  /** Add a cabinet by hand, on a platform whose numbers we type in. */
  createAccount: (body: {
    credentialId: number;
    title: string;
    externalId?: string;
    timezone?: string;
    currency?: string;
  }) =>
    request<{ id: number; title: string; externalId: string }>("/accounts", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  /** The platform blocked the cabinet: what is left goes back without commission. */
  blockAccount: (id: number) =>
    request<{ id: number; blockedAt: string; returned: string }>(`/accounts/${id}/block`, { method: "POST" }),
  unblockAccount: (id: number) => request<{ id: number }>(`/accounts/${id}/unblock`, { method: "POST" }),
  /** Remove a cabinet we typed in; its spend and assignment history go too. */
  deleteAccount: (id: number) => request<{ id: number }>(`/accounts/${id}`, { method: "DELETE" }),
  /** Type in a cabinet's spend for a range of days; the range is replaced. */
  setAccountSpend: (id: number, body: { start: string; end: string; amount: string }) =>
    request<{ days: number; perDay: string; total: string }>(`/accounts/${id}/spend`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  /** Read one cabinet's whole history now, instead of the trailing window. */
  backfillAccount: (id: number, days?: number) =>
    request<{ queued: boolean; provider: string; days: number }>(`/accounts/${id}/backfill`, {
      method: "POST",
      body: JSON.stringify(days ? { days } : {}),
    }),
  /** Balance of every cabinet that keeps money on itself. */
  cabinetBalances: (provider?: AdProvider) => request<CabinetBalance[]>(`/accounts/balances${qs({ provider })}`),
  /** Per-cabinet balance, for the platforms that keep money on the cabinet. */
  cabinets: (id: number, provider?: AdProvider) =>
    request<CabinetBalance[]>(`/clients/${id}/cabinets${qs({ provider })}`),
  /** Moves money from the client's account onto one of their cabinets. */
  allocate: (id: number, body: { adAccountId: number; amount: string; ratePercent?: string; note?: string }) =>
    request<{ cabinet: TopUp; account: TopUp }>(`/clients/${id}/allocations`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  /** Returns what is left on a cabinet to the client's account. */
  returnToAccount: (adAccountId: number, body: { amount?: string; ratePercent?: string; note?: string }) =>
    request<{ cabinet: TopUp; account: TopUp }>(`/accounts/${adAccountId}/return`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  /** Reverses a deposit booked in error. Nothing is sent to the client. */
  deleteTopUp: (clientId: number, topUpId: number) =>
    request<{ id: number; netAmount: string; commissionAmount: string }>(`/clients/${clientId}/top-ups/${topUpId}`, {
      method: "DELETE",
    }),
  addTopUp: (id: number, body: NewTopUp) =>
    request<TopUp>(`/clients/${id}/top-ups`, { method: "POST", body: JSON.stringify(body) }),
  /**
   * Corrects a booked transaction in place. Only the fields given change, and
   * either leg of a transfer corrects the pair. Nothing is sent to the client.
   */
  editTopUp: (clientId: number, topUpId: number, body: EditTopUpBody) =>
    request<EditTopUpResult>(`/clients/${clientId}/top-ups/${topUpId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  /** Moves the ticked parts of a client onto another client, in one transaction. */
  transferClient: (id: number, body: ClientTransferBody) =>
    request<ClientTransferResult>(`/clients/${id}/transfer`, { method: "POST", body: JSON.stringify(body) }),
  /** Changes a client's settings: `notificationsEnabled` silences their chat,
   *  `notificationPrefs` switches single kinds off and is merged server-side. */
  updateClient: (id: number, body: UpdateClientBody) =>
    request<ClientOverview>(`/clients/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  sendSummary: (id: number) =>
    request<{ queued: boolean; chatId: string }>(`/clients/${id}/summary`, { method: "POST" }),
  clientSpend: (id: number, window: number | { start: string; end: string } = 30) =>
    request<{ date: string; spend: string }[]>(
      `/clients/${id}/spend${qs(typeof window === "number" ? { days: window } : { start: window.start, end: window.end })}`,
    ),
  accounts: (provider?: AdProvider) => request<AdAccount[]>(`/accounts${qs({ provider })}`),

  /** The client workbook; with a range, cut to those dates and with campaign sheets. */
  exportClient: (id: number, range?: { start: string; end: string }) =>
    download(`/clients/${id}/export${qs({ start: range?.start, end: range?.end })}`, `client-${id}.xlsx`),
  /** Spend per campaign over a period, for a cabinet, a client, or everything. */
  campaignSpend: (q: CampaignSpendQuery) => request<CampaignSpendRow[]>(`/campaigns/spend${qs({ ...q })}`),
  exportCampaignSpend: (q: CampaignSpendQuery) =>
    download(`/campaigns/spend/export${qs({ ...q })}`, `campaigns-${q.start}_${q.end}.xlsx`),
  /** Admin-wide export: clients, cabinets and what is live right now. */
  exportStats: (start: string, end: string) =>
    download(`/export/stats?start=${start}&end=${end}`, `lumos-${start}_${end}.xlsx`),

  /** `scope` narrows to a client or a cabinet; empty covers everything. */
  campaigns: (scope: { clientId?: number; adAccountId?: number; provider?: AdProvider } = {}) =>
    request<Campaign[]>(`/campaigns${qs(scope)}`),
  budgetSummary: (scope: { clientId?: number; adAccountId?: number; provider?: AdProvider } = {}) =>
    request<BudgetSummary>(`/campaigns/budget${qs(scope)}`),
  /** Running budget per client (tracked cabinets), each with its cabinets. */
  budgetByClient: (provider?: AdProvider) => request<ClientBudget[]>(`/campaigns/budget/by-client${qs({ provider })}`),
  budgetByAccount: (scope: { clientId?: number; adAccountId?: number; provider?: AdProvider } = {}) =>
    request<AccountBudget[]>(`/campaigns/budget/by-account${qs(scope)}`),
  campaign: (id: number) => request<Campaign>(`/campaigns/${id}`),
  campaignDaily: (id: number, days = 30) => request<CampaignDailyPoint[]>(`/campaigns/${id}/daily?days=${days}`),

  depositWallets: () => request<DepositWallet[]>("/deposit-wallets"),
  createDepositWallet: (body: CreateDepositWalletBody) =>
    request<DepositWallet>("/deposit-wallets", { method: "POST", body: JSON.stringify(body) }),
  updateDepositWallet: (id: number, body: Partial<CreateDepositWalletBody>) =>
    request<DepositWallet>(`/deposit-wallets/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  walletExplorer: () => request<ExplorerWallet[]>("/wallet-explorer"),
  walletTransfers: (q: {
    walletId?: number;
    direction?: "in" | "out";
    start?: string;
    end?: string;
    q?: string;
    suspicious?: boolean;
    limit?: number;
    before?: number;
  }) =>
    request<{
      rows: WalletTransfer[];
      nextCursor: number | null;
      totals: { direction: string; asset: string; count: number; amount: string }[];
    }>(`/wallet-explorer/transfers${qs({ ...q, suspicious: q.suspicious ? "true" : undefined })}`),
  refreshWallets: () => request<{ queued: boolean }>("/wallet-explorer/refresh", { method: "POST" }),
  pnl: (range: { start: string; end: string }, groupBy: PnlGroupBy) =>
    request<PnlReport>(`/pnl${qs({ ...range, groupBy })}`),
  expenses: (range: { start: string; end: string }) => request<Expense[]>(`/expenses${qs({ ...range })}`),
  addExpense: (body: {
    date: string;
    amount: string;
    category: string;
    provider?: AdProvider | null;
    clientId?: number | null;
    note?: string;
    txHash?: string;
  }) => request<{ id: number }>("/expenses", { method: "POST", body: JSON.stringify(body) }),
  deleteExpense: (id: number) => request<{ id: number }>(`/expenses/${id}`, { method: "DELETE" }),
  clientForecast: (clientId: number) => request<ClientForecast>(`/clients/${clientId}/forecast`),
  telegramBots: () => request<TelegramBotInfo[]>("/telegram-bots"),
  inspectTelegramBot: (provider: AdProvider, token: string) =>
    request<TelegramBotInspection>(`/telegram-bots/${provider}/inspect`, {
      method: "POST",
      body: JSON.stringify({ token }),
    }),
  setTelegramBot: (provider: AdProvider, token: string) =>
    request<{ provider: AdProvider; username: string; live: boolean }>(`/telegram-bots/${provider}`, {
      method: "PUT",
      body: JSON.stringify({ token }),
    }),
  clearTelegramBot: (provider: AdProvider) =>
    request<{ provider: AdProvider; fallsBackToEnv: boolean }>(`/telegram-bots/${provider}`, { method: "DELETE" }),
  telegramBotCommands: (provider: AdProvider) =>
    request<{ scope: string; label: string; commands: { command: string; description: string }[] }[]>(
      `/telegram-bots/${provider}/commands`,
    ),
  syncTelegramBotCommands: (provider: AdProvider) =>
    request<{ provider: AdProvider; commands: number }>(`/telegram-bots/${provider}/commands/sync`, { method: "POST" }),
  checkTelegramBot: (provider: AdProvider) =>
    request<{ ok: boolean; username: string | null; name: string | null }>(`/telegram-bots/${provider}/check`, {
      method: "POST",
    }),
  mcpConnections: () => request<McpConnection[]>("/mcp/connections"),
  disconnectMcp: (clientId: string) =>
    request<{ revoked: number }>(`/mcp/connections/${encodeURIComponent(clientId)}`, { method: "DELETE" }),
  deleteDepositWallet: (id: number) => request<{ id: number }>(`/deposit-wallets/${id}`, { method: "DELETE" }),

  appRequests: () => request<AppRequest[]>("/app-requests"),
  setAppRequestStatus: (id: number, status: AppRequestStatus) =>
    request<{ id: number; status: string }>(`/app-requests/${id}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    }),
  deleteAppRequest: (id: number) => request<{ id: number }>(`/app-requests/${id}`, { method: "DELETE" }),

  /**
   * Mini app calls. They carry Telegram's signed init data instead of a
   * bearer token -- a webview has no dashboard session, and the signature is
   * what proves which chat is asking.
   */
  miniAppContext: (initData: string) =>
    request<MiniAppContext>("/miniapp/context", { headers: { "X-Telegram-Init-Data": initData } }),
  createAppRequest: (initData: string, body: CreateAppRequestBody) =>
    request<{ created: number }>("/miniapp/app-requests", {
      method: "POST",
      headers: { "X-Telegram-Init-Data": initData },
      body: JSON.stringify(body),
    }),
  setTracking: (id: number, isTracked: boolean) =>
    request<{ id: number; isTracked: boolean }>(`/accounts/${id}/tracking`, {
      method: "PATCH",
      body: JSON.stringify({ isTracked }),
    }),
  assign: (id: number, clientId: number | null) =>
    request<{ id: number; clientId: number | null }>(`/accounts/${id}/assignment`, {
      method: "PATCH",
      body: JSON.stringify({ clientId }),
    }),
  /** Daily spend for a cabinet: the last `days`, or exactly the picked window. */
  accountSpend: (id: number, window: number | { start: string; end: string } = 30) =>
    request<SpendPoint[]>(
      `/accounts/${id}/spend${qs(typeof window === "number" ? { days: window } : { start: window.start, end: window.end })}`,
    ),
  runSync: () => request<{ queued: boolean; trackedAccounts: number }>("/sync/run", { method: "POST" }),
  syncRuns: () => request<SyncRun[]>("/sync/runs"),
  /** Full sync history, newest first; pass the previous page's `nextCursor` as `before`. */
  syncLog: (q: SyncLogQuery & { limit?: number; before?: number }) =>
    request<{ rows: SyncLogRow[]; nextCursor: number | null }>(`/logs/sync${qs({ ...q })}`),
  syncLogSummary: (q: SyncLogQuery) => request<SyncLogSummary>(`/logs/sync/summary${qs({ ...q })}`),
  /** Tell the server an admin opened a page; it dedupes repeat visits itself. */
  auditView: (entityType: "account" | "client" | "campaign", entityId: number, tab?: string) =>
    request<{ recorded: boolean }>("/audit/view", {
      method: "POST",
      body: JSON.stringify({ entityType, entityId: String(entityId), tab: tab || undefined }),
    }),
  adminLog: (q: AdminLogQuery & { limit?: number; before?: number }) =>
    request<{ rows: AdminLogRow[]; nextCursor: number | null }>(`/logs/admin${qs({ ...q })}`),
  adminLogActors: () => request<{ id: number; email: string; name: string | null }[]>("/logs/admin/actors"),
  notificationLog: (q: { q?: string; start?: string; end?: string; limit?: number; before?: number }) =>
    request<{ rows: NotificationLogRow[]; nextCursor: number | null }>(`/logs/notifications${qs({ ...q })}`),
  rotateCredential: (id: number, apiKey: string, label?: string) =>
    request<Credential>(`/credentials/${id}`, { method: "PUT", body: JSON.stringify({ apiKey, label }) }),
  settings: () => request<Settings>("/settings"),
  saveSettings: (patch: Partial<Settings>) =>
    request<Settings>("/settings", { method: "PUT", body: JSON.stringify(patch) }),
  credentials: () => request<Credential[]>("/credentials"),
  addCredential: (body: {
    provider: AdProvider;
    label: string;
    apiKey?: string;
    advertiserIds?: string[];
    appId?: string;
    secret?: string;
    authCode?: string;
    keyId?: string;
    organizationId?: string;
  }) => request<Credential>("/credentials", { method: "POST", body: JSON.stringify(body) }),
  deleteCredential: (id: number) => request<{ id: number }>(`/credentials/${id}`, { method: "DELETE" }),
  setCredentialStatus: (id: number, status: "active" | "disabled") =>
    request<Credential>(`/credentials/${id}/status`, { method: "PATCH", body: JSON.stringify({ status }) }),
  /** Runs the provider's discovery now instead of at the next scheduled tick. */
  syncProvider: (provider: AdProvider) =>
    request<{ provider: string }>("/credentials/sync", { method: "POST", body: JSON.stringify({ provider }) }),
};

export interface BroadcastButtonInput {
  text: string;
  type: "url" | "copy_text" | "switch_inline" | "switch_inline_current" | "callback";
  value: string;
  /** Bot API 9.4 colour; "" leaves the client default. */
  style: "" | "primary" | "success" | "danger";
}

export interface NewBroadcast {
  kind: string;
  audience: "all" | "selected" | "one";
  targetClientIds: number[];
  text?: string;
  mediaPath?: string;
  mediaName?: string;
  mediaMime?: string;
  buttons: BroadcastButtonInput[][];
  disablePreview: boolean;
  silent: boolean;
  scheduledAt?: string | null;
}

export interface Broadcast {
  id: number;
  kind: string;
  audience: string;
  targetClientIds: number[];
  text: string | null;
  mediaName: string | null;
  mediaMime: string | null;
  buttons: BroadcastButtonInput[][];
  status: string;
  scheduledAt: string | null;
  totalCount: number;
  sentCount: number;
  failedCount: number;
  error: string | null;
  createdAt: string;
  finishedAt: string | null;
}

export interface BroadcastDelivery {
  id: number;
  clientTitle: string | null;
  chatId: string;
  status: string;
  messageId: number | null;
  error: string | null;
  sentAt: string | null;
  deletedAt: string | null;
}

export interface ExplorerWallet {
  id: number;
  network: string;
  token: string;
  address: string;
  isActive: boolean;
  providers: string[];
  note: string | null;
  /** Whether the explorer can read this network. */
  supported: boolean;
  syncedAt: string | null;
  error: string | null;
  transfers: number;
  lastTransferAt: string | null;
  balances: { asset: string; amount: string; verified: boolean; usdPrice: string | null; amountUsd: string | null }[];
  /** Verified holdings in USD; stablecoins at par. */
  totalUsd: string;
  /** Some holding had no price, so the total is incomplete. */
  totalUsdPartial: boolean;
  flows: {
    direction: "in" | "out";
    asset: string;
    last24h: string;
    last7d: string;
    last30d: string;
    count30d: number;
  }[];
}

export interface WalletTransfer {
  id: number;
  walletId: number;
  network: string;
  walletToken: string;
  walletAddress: string;
  txHash: string;
  direction: "in" | "out" | "self";
  asset: string;
  tokenContract: string | null;
  amount: string;
  amountUsd: string | null;
  counterparty: string | null;
  blockTime: string;
  blockNumber: number | null;
  success: boolean;
  verified: boolean;
  suspicious: boolean;
  /** The deposit booked with this transaction's hash, when there is one. */
  /** The client top-up booked with this transaction's hash: a deposit for incoming, a refund for outgoing. */
  linked: {
    topUpId: number;
    clientId: number;
    clientTitle: string;
    provider: string;
    type: "deposit" | "refund";
  } | null;
  /** The expense booked with this transaction's hash. */
  expense: { id: number; category: string; amount: string } | null;
}

export type PnlGroupBy = "month" | "client" | "platform";

export interface PnlFigures {
  revenue: string;
  expenses: string;
  profit: string;
  depositsGross: string;
  refundsPaid: string;
  clientSpend: string;
}

export interface PnlReport {
  groupBy: PnlGroupBy;
  summary: {
    range: { start: string; end: string };
    revenue: string;
    commissionOnDeposits: string;
    commissionOnRefunds: string;
    expenses: string;
    profit: string;
    marginPercent: string | null;
    depositsGross: string;
    depositsNet: string;
    depositsCount: number;
    refundsPaid: string;
    refundsCount: number;
    payingClients: number;
    effectiveRatePercent: string | null;
    clientSpend: string;
    clientBalancesAtEnd: string;
  };
  rows: (PnlFigures & { key: string; label: string })[];
}

export interface Expense {
  id: number;
  date: string;
  amount: string;
  category: string;
  provider: string | null;
  clientId: number | null;
  clientTitle: string | null;
  note: string | null;
  txHash: string | null;
  createdByEmail: string | null;
  createdAt: string;
}

export interface ForecastEstimate {
  balance: string;
  spendLast7: string;
  avgDailySpend: string;
  /** Last 7 full days against the 7 before them. */
  trendPercent: string | null;
  liveDailyBudget: string;
  daysLeft: number | null;
  runsOutOn: string | null;
  daysLeftAtBudget: number | null;
  runsOutAtBudgetOn: string | null;
  status: "ok" | "low" | "critical" | "exhausted" | "idle";
}

export interface ClientForecast {
  asOf: string;
  platforms: (ForecastEstimate & { provider: string; cabinets: number; spendToday: string; daysWithData: number })[];
  /** Across platforms; only when there is more than one. */
  total: ForecastEstimate | null;
}

export interface TelegramBotInfo {
  provider: AdProvider;
  /** Whether this platform's bot exists; the rest only store a token for later. */
  live: boolean;
  /** Where the token in use comes from; null when there is none. */
  source: "settings" | "env" | null;
  botId: string | null;
  username: string | null;
  name: string | null;
  tokenHint: string | null;
  verifiedAt: string | null;
  updatedAt: string | null;
  updatedByEmail: string | null;
}

export interface TelegramBotInspection {
  id: string;
  username: string;
  name: string;
  currentBotId: string | null;
  currentUsername: string | null;
  /** null when the platform has no bot yet to compare with. */
  sameBot: boolean | null;
  usedByProvider: AdProvider | null;
}

export interface McpConnection {
  clientId: string;
  clientName: string | null;
  connectedAt: string;
  lastRefreshedAt: string;
  expiresAt: string | null;
}

export interface Me {
  adminId: number;
  email: string;
  role: { id: number; slug: string; name: string } | null;
  permissions: string[];
}

export interface PermissionDef {
  key: string;
  group: string;
  label: string;
}

export interface Role {
  id: number;
  slug: string;
  name: string;
  permissions: string[];
  isSystem: boolean;
  userCount: number;
  createdAt: string;
}

export interface AdminUser {
  id: number;
  email: string;
  name: string | null;
  isActive: boolean;
  roleId: number | null;
  roleName: string | null;
  roleSlug: string | null;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface ProviderSpend {
  /** Spend inside the picked window. */
  range: string;
  today: string;
  yesterday: string;
  week: string;
  month: string;
  total: string;
  accountsSpendingToday: number;
}

export interface Stats {
  rangeSpend: string;
  rangeAccounts: number;
  /** A platform's own figures, keyed by provider. */
  byProvider: Record<string, ProviderSpend>;
  /** Money keyed by provider — "moloco", "facebook", … */
  spendTodayByProvider: Record<string, string>;
  spendYesterdayByProvider: Record<string, string>;
  totalSpendByProvider: Record<string, string>;
  clientCount: number;
  accountCount: number;
  trackedAccountCount: number;
  unassignedAccountCount: number;
  accountsSpendingToday: number;
  commissionEarned: string;
  totalSpend: string;
  totalFunded: string;
  totalFundedGross: string;
  outstandingBalance: string;
  spendToday: string;
  spendYesterday: string;
  spend7d: string;
  spendMonth: string;
  spendAvg7d: string;
  impressionsToday: number;
  clicksToday: number;
  lastSyncedAt: string | null;
}

export interface TopAccount {
  id: number;
  title: string;
  clientTitle: string | null;
  spendToday: string;
  totalSpend: string;
}

export interface ClientOverview {
  id: number;
  telegramChatId: string;
  title: string;
  chatType: string;
  status: "active" | "paused" | "archived";
  /** Off silences the client's own chat: summaries, warnings, deposits, broadcasts. */
  notificationsEnabled: boolean;
  /** Per-kind switches under the master above. Sparse: absent means on. */
  notificationPrefs: NotificationPrefs;
  createdAt: string;
  accountCount: number;
  toppedUpGross: string;
  commissionEarned: string;
  netFunded: string;
  totalSpend: string;
  availableBalance: string;
  ratePercent: string;
}

export interface Tier {
  thresholdAmount: string;
  ratePercent: string;
}

/**
 * One stretch of time a cabinet worked for a client, to the minute. Half-open:
 * a handover ends one period and starts the next at the same instant, and the
 * day it happened is split between them by the hours each side held.
 */
export interface AccountAssignment {
  id: number;
  adAccountId: number;
  clientId: number;
  accountTitle: string;
  externalId: string;
  provider: AdProvider;
  clientTitle: string;
  startedAt: string;
  /** null while the cabinet is still theirs. */
  endedAt: string | null;
  note: string | null;
  /** What the cabinet spent while it was theirs. */
  spend: string;
  firstSpendDay: string | null;
  lastSpendDay: string | null;
  createdAt: string;
}

export interface CabinetBalance {
  adAccountId: number;
  clientId: number;
  clientTitle: string;
  provider: AdProvider;
  title: string;
  externalId: string;
  currency: string;
  isTracked: boolean;
  blockedAt: string | null;
  autoDisabledAt: string | null;
  disableReason: string | null;
  syncedAt: string | null;
  /** Moved onto this cabinet from the account, after commission. */
  netFunded: string;
  spendToday: string;
  totalSpend: string;
  availableBalance: string;
  /** The rate of the last funding, which is what a return gives back. */
  lastRatePercent: string | null;
  lastFundedAt: string | null;
  clientStatus: string;
  lowBalanceNotifiedAt: string | null;
  strandedNotifiedAt: string | null;
}

export interface NewTopUp {
  type: "deposit" | "refund";
  /** Moloco lands on its master balance with commission; the rest on that platform's account. */
  provider: AdProvider;
  /** Deposit: what the client paid. Refund: what comes off their balance. */
  grossAmount: string;
  /** Refund only, signed: + returns commission on top, − keeps a fee. */
  refundRatePercent?: string;
  paymentMethod: "crypto" | "fiat";
  network?: string;
  token?: string;
  txHash?: string;
  note?: string;
}

export interface TopUp {
  id: number;
  clientId: number;
  /** Amounts are negative on a refund, and on the paying side of a transfer. */
  type: TransactionType;
  provider: AdProvider;
  /** Set on the cabinet side of a transfer between the account and a cabinet; null means the account. */
  adAccountId: number | null;
  /** Both sides of one transfer share it; they are reversed together. */
  groupId: string | null;
  grossAmount: string;
  ratePercent: string;
  commissionAmount: string;
  netAmount: string;
  cumulativeBefore: string;
  paymentMethod: "crypto" | "fiat";
  network: string | null;
  token: string | null;
  txHash: string | null;
  note: string | null;
  createdAt: string;
}

export interface AdAccount {
  id: number;
  provider: string;
  externalId: string;
  title: string;
  timezone: string;
  currency: string;
  clientId: number | null;
  clientTitle: string | null;
  isTracked: boolean;
  /** Set when the platform blocked the cabinet; its remainder went back then. */
  blockedAt: string | null;
  syncedAt: string | null;
  /** Set when the worker switched collection off itself. */
  autoDisabledAt: string | null;
  /** Why collection is off: the platform's refusal, or "Выключен вручную". */
  disableReason: string | null;
  totalSpend: string;
}

export interface SpendPoint {
  date: string;
  spend: string;
  impressions: number;
  clicks: number;
  isFinal: boolean;
}

export interface Settings {
  syncCron: string;
  discoverCron: string;
  campaignCron: string;
  facebookCron: string;
  tiktokCron: string;
  unityCron: string;
  walletsCron: string;
  summaryCronMorning: string;
  summaryCronEvening: string;
  timezone: string;
  trailingWindowDays: number;
  lowBalanceThreshold: string;
  /** Consecutive permission failures before a cabinet switches itself off; 0 is never. */
  autoDisableAfterErrors: number;
  /** Chats the bot must never register as clients. */
  ignoredChatIds: string[];
}

export interface SyncLogQuery {
  status?: "ok" | "error";
  provider?: AdProvider;
  kind?: "spend" | "campaigns" | "discover" | "wallets";
  adAccountId?: number;
  q?: string;
  start?: string;
  end?: string;
}

export interface SyncLogRow {
  id: number;
  kind: string;
  /** null for runs that belong to no ad platform, e.g. wallet reads. */
  provider: AdProvider | null;
  status: string;
  rowsUpserted: number;
  error: string | null;
  durationMs: number | null;
  windowStart: string | null;
  windowEnd: string | null;
  adAccountId: number | null;
  accountTitle: string | null;
  accountExternalId: string | null;
  clientId: number | null;
  clientTitle: string | null;
  walletId: number | null;
  walletNetwork: string | null;
  walletAddress: string | null;
  createdAt: string;
}

export interface SyncLogSummary {
  total: number;
  errors: number;
  avgDurationMs: number;
  rowsUpserted: number;
  lastAt: string | null;
  lastErrorAt: string | null;
}

export type AdminLogCategory = "view" | "change" | "money" | "export" | "auth";

export type AdminLogSource = "dashboard" | "mcp";

export interface AdminLogQuery {
  adminId?: number;
  source?: AdminLogSource;
  category?: AdminLogCategory;
  entityType?: string;
  entityId?: string;
  q?: string;
  start?: string;
  end?: string;
}

export interface AdminLogRow {
  id: number;
  adminId: number | null;
  adminEmail: string | null;
  adminName: string | null;
  /** Where the action came from: the dashboard, or an AI client over MCP. */
  source: AdminLogSource;
  category: AdminLogCategory;
  action: string;
  entityType: string | null;
  entityId: string | null;
  entityLabel: string | null;
  method: string | null;
  path: string | null;
  statusCode: number | null;
  ip: string | null;
  userAgent: string | null;
  details: unknown;
  createdAt: string;
}

export interface NotificationLogRow {
  id: number;
  kind: string;
  periodKey: string;
  clientId: number;
  clientTitle: string | null;
  sentAt: string;
}

export interface SyncRun {
  id: number;
  kind: string;
  status: string;
  rowsUpserted: number;
  error: string | null;
  durationMs: number | null;
  accountTitle: string | null;
  createdAt: string;
}

export interface Credential {
  id: number;
  provider: string;
  label: string;
  /** Cabinets this token owns; deleting is refused while it is above zero. */
  accountCount: number;
  status: string;
  lastAuthAt: string | null;
  lastError: string | null;
  createdAt: string;
}
